import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'
import { useNavigate } from 'react-router'

import { reportError } from '@/domains/observability'
import { useScopePrefix } from '@/domains/setup'

import { createConversation } from './api'
import { CONVERSATIONS_LIST_KEY } from './keys'
import {
  type Action,
  type FormState,
  NewConversationContext
} from './new-conversation-context'

const initialState: FormState = {
  subject: '',
  body: '',
  recipients: [],
  attachments: [],
  error: '',
  submitting: false
}

function reducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case 'SET_SUBJECT':
      return { ...state, subject: action.payload }
    case 'SET_BODY':
      return { ...state, body: action.payload }
    case 'SET_RECIPIENTS':
      return { ...state, recipients: action.payload }
    case 'SET_ATTACHMENTS':
      return { ...state, attachments: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, submitting: false }
    case 'CLEAR_ERROR':
      return { ...state, error: '' }
    case 'SUBMIT_START':
      return { ...state, submitting: true, error: '' }
    case 'SUBMIT_END':
      return { ...state, submitting: false }
    default:
      return state
  }
}

export function NewConversationFormProvider({
  children
}: {
  children: ReactNode
}) {
  const navigate = useNavigate()
  const prefix = useScopePrefix()
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, initialState)

  const isValid =
    state.subject.trim().length > 0 &&
    state.body.trim().length > 0 &&
    state.recipients.length > 0 &&
    state.attachments.every(attachment => attachment.status === 'ready')

  const mutation = useMutation({
    mutationFn: createConversation,
    onSuccess: async created => {
      await queryClient.invalidateQueries({ queryKey: CONVERSATIONS_LIST_KEY })
      dispatch({ type: 'SUBMIT_END' })
      void navigate(`${prefix}/messages/${created.id}`)
    },
    onError: err => {
      reportError(err)
      dispatch({
        type: 'SET_ERROR',
        payload: 'Failed to start conversation. Try again.'
      })
    }
  })

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    dispatch({ type: 'CLEAR_ERROR' })

    if (!state.subject.trim() || !state.body.trim()) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Subject and first message are required.'
      })
      return
    }
    if (state.recipients.length === 0) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Pick at least one recipient.'
      })
      return
    }
    if (state.attachments.some(attachment => attachment.status !== 'ready')) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Wait for attachments to finish uploading.'
      })
      return
    }

    dispatch({ type: 'SUBMIT_START' })
    mutation.mutate({
      subject: state.subject.trim(),
      firstMessage: state.body.trim(),
      participantCompanyIds: state.recipients.map(recipient => recipient.id),
      attachments: state.attachments
        .filter(attachment => attachment.status === 'ready')
        .map(attachment => ({
          attachmentId: attachment.attachmentId,
          fileName: attachment.fileName,
          contentType: attachment.contentType,
          byteSize: attachment.byteSize
        }))
    })
  }

  return (
    <NewConversationContext.Provider
      value={{ state, dispatch, isValid, handleSubmit }}
    >
      {children}
    </NewConversationContext.Provider>
  )
}
